import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  CATEGORY_META,
  resolveCategoryMeta,
} from "./categoryDirectoryConfig";
import {
  COMMUNITY_META,
  resolveCommunityMeta,
} from "./communityDirectoryConfig";

const SITE_URL = "https://www.goourhub.com";
const DEFAULT_IMAGE = `${SITE_URL}/goourhub-logo.png`;

const PUBLIC_CORE_META = {
  "/": {
    title: "GoOurHub | San Antonio Events, Businesses & Local Marketplace",
    description:
      "Discover San Antonio businesses, cultural communities, local events, deals, jobs, and marketplace listings on GoOurHub.",
    canonicalPath: "/",
  },

  // /hub currently renders the exact same Hub component as /
  // so / is the preferred canonical URL.
  "/hub": {
    title: "GoOurHub | San Antonio Events, Businesses & Local Marketplace",
    description:
      "Discover San Antonio businesses, cultural communities, local events, deals, jobs, and marketplace listings on GoOurHub.",
    canonicalPath: "/",
  },

  "/hubmart": {
    title: "HubMart | Buy, Sell & Find Local Deals in San Antonio | GoOurHub",
    description:
      "Browse HubMart for local items, jobs, free finds, vehicles, rentals, and community marketplace listings in San Antonio.",
    canonicalPath: "/HubMart",
  },

  "/categories": {
    title: "San Antonio Business Directory | Local Services & Shops | GoOurHub",
    description:
      "Explore local restaurants, shops, services, food trucks, groceries, professionals, and businesses in San Antonio on GoOurHub.",
    canonicalPath: "/categories",
  },

  "/connect": {
    title: "Connect with GoOurHub | Community Updates & Social Channels",
    description:
      "Connect with GoOurHub for local community updates, businesses, events, marketplace finds, and opportunities in San Antonio.",
    canonicalPath: "/connect",
  },

  "/contact": {
    title: "Contact GoOurHub | San Antonio Community Platform",
    description:
      "Contact GoOurHub for questions about local businesses, communities, events, HubMart, partnerships, and support.",
    canonicalPath: "/contact",
  },

  "/faq": {
    title: "GoOurHub FAQ | Businesses, Community, HubMart & Partnerships",
    description:
      "Find answers about GoOurHub accounts, business listings, HubMart, community discovery, partnerships, pricing, and support.",
    canonicalPath: "/faq",
  },

  "/about-us": {
    title: "About GoOurHub | Connecting San Antonio Communities",
    description:
      "Learn about GoOurHub and our mission to connect San Antonio communities with local businesses, events, services, and opportunities.",
    canonicalPath: "/about-us",
  },

  "/pricing": {
    title: "GoOurHub Pricing | Business & Partner Listings",
    description:
      "Explore GoOurHub pricing and options for businesses and partners who want to reach local communities in San Antonio.",
    canonicalPath: "/Pricing",
  },

  "/partner-signup": {
    title: "Partner with GoOurHub | Reach San Antonio Communities",
    description:
      "Join GoOurHub as a partner and connect your business or organization with local communities across San Antonio.",
    canonicalPath: "/partner-signup",
  },

  "/privacy": {
    title: "Privacy Policy | GoOurHub",
    description:
      "Read the GoOurHub privacy policy and learn how information is handled on the GoOurHub platform.",
    canonicalPath: "/privacy",
  },

  "/terms": {
    title: "Terms of Use | GoOurHub",
    description:
      "Read the terms of use for the GoOurHub website, community platform, business listings, and marketplace.",
    canonicalPath: "/terms",
  },

  "/fiesta": {
    title: "Fiesta San Antonio | Local Finds & Community Highlights | GoOurHub",
    description:
      "Explore Fiesta-related local finds, community highlights, and San Antonio connections on GoOurHub.",
    canonicalPath: "/Fiesta",
  },
};

const NOINDEX_PATHS = new Set([
  "/landing",
  "/hub/search",
  "/business-admin",
  "/post-offer",
  "/needhelp",
  "/signinmodal",
  "/signupmodal",
  "/thank-you-partner",
  "/vendor-thankyou",
  "/user-thankyou",
  "/zelle-confirmation",
  "/zelle-thank-you",
  "/profile",
  "/fiestamedaltile",
  "/promosection",
  "/comingsoondirectory",
  "/vendor-template1",
  "/vendor-template2",
  "/vendor-template3",
]);

const LEGACY_COMMUNITY_SLUGS = {
  "/mexican": "mexican",
  "/american": "american",
  "/ethiopian": "ethiopian",
  "/eritrean": "eritrean",
  "/african": "african",
  "/turkish": "turkish",
  "/syrian": "syrian",
  "/lebanese": "lebanese",
  "/palestinian": "palestinian",
  "/persian": "persian",
  "/armenian": "armenian",
  "/nigerian": "nigerian",
  "/middleeastern": "middle-eastern",
  "/indian": "indian",
  "/chinese": "chinese",
  "/japanese": "japanese",
  "/korean": "korean",
  "/thai": "thai",
  "/european": "european",
  "/algerian": "algerian",
  "/argentinian": "argentinian",
  "/australian": "australian",
  "/british": "british",
  "/hawaiian": "hawaiian",
  "/jamaican": "jamaican",
  "/nepali": "nepali",
  "/vietnamese": "vietnamese",
  "/senegalese": "senegalese",
  "/trinidadian": "trinidadian",
  "/cameroonian": "cameroonian",
  "/azerbaijani": "azerbaijani",
};

const LEGACY_CATEGORY_SLUGS = {
  "/visitresturants": "restaurants",
  "/visitgroceries": "groceries",
  "/visitfoodtrucks": "foodtruck",
  "/visitkosherplaces": "kosher",
  "/visitveganplaces": "vegan",
  "/cleaning-services": "home-help",
  "/itservices": "it-services",
  "/realtor": "realtors",
  "/photography": "photography",
  "/gardening": "gardening",
  "/catering": "catering",
  "/jewelry": "jewelry",
  "/childcare": "child-care",
  "/electrical": "electrical",
  "/plumbing": "plumbing",
  "/elderlycare": "elderly-care",
};

const PUBLIC_VENDOR_ROUTES = {
  "/pitaplus": ["Pita Plus", "/pitaplus"],
  "/selamfoodmart": ["Selam Food Mart", "/selamfoodmart"],
  "/africanvillage": ["African Village", "/AfricanVillage"],
  "/vaduaplu": ["Vadu Aplu", "/vaduaplu"],
  "/tacoblvd": ["Taco Blvd", "/tacoblvd"],
  "/un-gwan-gway": ["Un Gwan Gway", "/Un-Gwan-Gway"],
  "/rigoplant": ["Rigo Plant", "/RigoPlant"],
  "/trinkettrunk": ["Trinket Trunk", "/TrinketTrunk"],
  "/ictoyz": ["Ic Toyz", "/IcToyz"],
  "/twobunnies": ["Two Bunnies", "/TwoBunnies"],
  "/g3production": ["G3 Production", "/G3Production"],
  "/jameswreaths": ["James Wreaths", "/JamesWreaths"],
  "/alexsword": ["Alex Sword", "/AlexSword"],
  "/starrmurals": ["Starr Murals", "/StarrMurals"],
  "/nenaclothing": ["Nena Clothing", "/NenaClothing"],
  "/fevenrealtor": ["Feven Realtor", "/FevenRealtor"],
  "/peterselitecleaning": [
    "Peters Elite Cleaning",
    "/peterselitecleaning",
  ],
  "/twolittlemonkeys": ["Two Little Monkeys", "/TwoLittleMonkeys"],
};

const normalizePath = (pathname) => {
  const clean = String(pathname || "/").replace(/\/+$/, "");
  return (clean || "/").toLowerCase();
};

const getKnownCategoryBySlug = (slug) =>
  CATEGORY_META.find((item) => item.slug === String(slug).toLowerCase());

const getKnownCommunityBySlug = (slug) =>
  COMMUNITY_META.find((item) => item.slug === String(slug).toLowerCase());

const categoryMetaFor = (slug, canonicalSlug = slug) => {
  const known = getKnownCategoryBySlug(slug);

  if (!known) {
    return null;
  }

  const meta = resolveCategoryMeta(slug);

  return {
    title: `${meta.label} in San Antonio | GoOurHub`,
    description: `Find ${meta.label.toLowerCase()} in San Antonio and discover local businesses, services, and community recommendations on GoOurHub.`,
    canonicalPath: `/categories/${canonicalSlug}`,
  };
};

const communityMetaFor = (slug, canonicalSlug = slug) => {
  const known = getKnownCommunityBySlug(slug);

  if (!known) {
    return null;
  }

  const meta = resolveCommunityMeta(slug);

  return {
    title: `${meta.label} Community in San Antonio | Events & Businesses | GoOurHub`,
    description: `Discover ${meta.label} community businesses, events, organizations, and local resources in San Antonio on GoOurHub.`,
    canonicalPath: `/communities/${canonicalSlug}`,
    robots: meta.comingSoon === true ? "noindex, follow" : "index, follow",
  };
};

const buildRouteMeta = (pathname) => {
  const path = normalizePath(pathname);

  if (NOINDEX_PATHS.has(path)) {
    return {
      title: "GoOurHub",
      description:
        "GoOurHub connects people with local businesses, communities, events, deals, jobs, and marketplace listings.",
      canonicalPath: null,
      robots: "noindex, nofollow",
    };
  }

  if (PUBLIC_CORE_META[path]) {
    return {
      ...PUBLIC_CORE_META[path],
      robots: "index, follow",
    };
  }

  if (path.startsWith("/categories/")) {
    const slug = path.slice("/categories/".length);
    const meta = categoryMetaFor(slug);

    return (
      meta || {
        title: "Business Directory | GoOurHub",
        description:
          "Explore local businesses and services on GoOurHub.",
        canonicalPath: null,
        robots: "noindex, follow",
      }
    );
  }

  if (path.startsWith("/communities/")) {
    const slug = path.slice("/communities/".length);
    const meta = communityMetaFor(slug);

    return (
      meta || {
        title: "Community Directory | GoOurHub",
        description:
          "Explore local communities, businesses, events, and resources on GoOurHub.",
        canonicalPath: null,
        robots: "noindex, follow",
      }
    );
  }

  if (LEGACY_CATEGORY_SLUGS[path]) {
    const canonicalSlug = LEGACY_CATEGORY_SLUGS[path];
    const meta = categoryMetaFor(canonicalSlug, canonicalSlug);

    return (
      meta || {
        title: "Business Directory | GoOurHub",
        description:
          "Explore local businesses and services on GoOurHub.",
        canonicalPath: null,
        robots: "noindex, follow",
      }
    );
  }

  if (LEGACY_COMMUNITY_SLUGS[path]) {
    const canonicalSlug = LEGACY_COMMUNITY_SLUGS[path];
    const meta = communityMetaFor(canonicalSlug, canonicalSlug);

    return (
      meta || {
        title: "Community Directory | GoOurHub",
        description:
          "Explore local communities, businesses, events, and resources on GoOurHub.",
        canonicalPath: null,
        robots: "noindex, follow",
      }
    );
  }

  if (PUBLIC_VENDOR_ROUTES[path]) {
    const [name, canonicalPath] = PUBLIC_VENDOR_ROUTES[path];

    return {
      title: `${name} | Local Business in San Antonio | GoOurHub`,
      description: `Discover ${name} on GoOurHub and explore local businesses, services, and community connections in San Antonio.`,
      canonicalPath,
      robots: "index, follow",
    };
  }

  // Any route not explicitly approved above is kept out of Google's index.
  // This is important because the app's "*" route currently renders <Hub />,
  // which otherwise could create many duplicate/soft-404 URLs.
  return {
    title: "GoOurHub",
    description:
      "GoOurHub connects people with local businesses, communities, events, deals, jobs, and marketplace listings.",
    canonicalPath: null,
    robots: "noindex, nofollow",
  };
};

const upsertMeta = (attribute, key, content) => {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
};

const setCanonical = (canonicalUrl) => {
  let link = document.head.querySelector('link[rel="canonical"]');

  if (!canonicalUrl) {
    if (link) {
      link.remove();
    }
    return;
  }

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", canonicalUrl);
};

function SEOManager() {
  const location = useLocation();

  useEffect(() => {
    const routeMeta = buildRouteMeta(location.pathname);

    const canonicalUrl = routeMeta.canonicalPath
      ? `${SITE_URL}${routeMeta.canonicalPath}`
      : null;

    document.title = routeMeta.title;

    upsertMeta("name", "description", routeMeta.description);
    upsertMeta("name", "robots", routeMeta.robots || "index, follow");

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "GoOurHub");
    upsertMeta("property", "og:title", routeMeta.title);
    upsertMeta("property", "og:description", routeMeta.description);
    upsertMeta("property", "og:image", DEFAULT_IMAGE);

    if (canonicalUrl) {
      upsertMeta("property", "og:url", canonicalUrl);
    } else {
      const ogUrl = document.head.querySelector('meta[property="og:url"]');
      if (ogUrl) {
        ogUrl.remove();
      }
    }

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", routeMeta.title);
    upsertMeta("name", "twitter:description", routeMeta.description);
    upsertMeta("name", "twitter:image", DEFAULT_IMAGE);

    setCanonical(canonicalUrl);
  }, [location.pathname]);

  return null;
}

export default SEOManager;
